import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, type DataTableColumn } from "@/components/ui/DataTable/index";
import { Combobox } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import {
  useListAchReviewInstances,
  useApproveAchReviewStageInstance,
  type DocumentReviewInstanceSummary,
  type DocumentReviewInstanceStatus,
  type DocumentReviewStageInstanceSummary,
  type DocumentReviewStageInstanceStatus,
  type ApproveAchReviewStageInstancePayload,
} from "@/lib/api/ach";
import { CommonDestinationTimezone } from "@/lib/utils";
import moment from "moment-timezone";
import { CheckCircle, XCircle, FileIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useStytchMember } from "@stytch/react/b2b";
import { getUserDbId } from "@/lib/models/auth";
import { useToast } from "@/components/ui/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { RoutePath } from "@/section/router";
import {
  getAchInstanceStatusBadge,
  getAchStageStatusBadge,
  getAchCurrentStage,
} from "@/section/ach/components/reviewInstanceUtils";

const statusOptions = [
  { value: "", label: "All Statuses" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export function AchReviewInstancesPage() {
  const { member } = useStytchMember();
  const currentUserId = member ? getUserDbId(member) : null;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const approveStage = useApproveAchReviewStageInstance();
  const navigate = useNavigate();

  const [status, setStatus] = useState<DocumentReviewInstanceStatus | "">("");
  const [page, setPage] = useState(1);
  const [selectedInstance, setSelectedInstance] =
    useState<DocumentReviewInstanceSummary | null>(null);
  const limit = 50;

  const listResult = useListAchReviewInstances(
    {
      status: status || undefined,
      page,
      limit,
    },
    { enabled: true }
  );

  const isLoading = listResult.isLoading;
  const instances = listResult?.data?.data?.instances || [];
  const total = listResult?.data?.data?.total || 0;

  const navigateToPreview = useCallback(
    (instance: DocumentReviewInstanceSummary, type: "sent" | "final") => {
      const templateId = instance.AchDocumentInstance?.ach_document_id;
      const instanceId = instance.AchDocumentInstance?.id;
      
      if (!templateId || !instanceId) {
        return;
      }

      const pathTemplate =
        type === "sent"
          ? RoutePath.AchDocumentsInstanceSentPreview
          : RoutePath.AchDocumentsInstanceFinalPreview;

      const path = pathTemplate
        .replace(":templateId", templateId)
        .replace(":instanceId", instanceId);

      navigate(path);
    },
    [navigate],
  );

  const columns: DataTableColumn<DocumentReviewInstanceSummary>[] = [
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => getAchInstanceStatusBadge(row.original.status),
      size: 150,
    },
    {
      accessorKey: "AchDocumentInstance.customer_name",
      header: "Customer",
      cell: ({ row }) => {
        const instance = row.original;
        return (
          <div>
            {instance.AchDocumentInstance?.customer_name || (
              <span className="text-muted-foreground">N/A</span>
            )}
          </div>
        );
      },
      size: 200,
    },
    {
      accessorKey: "StageInstances",
      header: "Stages",
      cell: ({ row }) => {
        const instance = row.original;
        const totalStages = instance.StageInstances.length;
        const completedStages = instance.StageInstances.filter(
          (s) => s.status === "approved" || s.status === "denied" || s.status === "skipped"
        ).length;
        return (
          <div className="text-sm">
            {completedStages} / {totalStages}
          </div>
        );
      },
      size: 100,
    },
    {
      id: "latest_stage_status",
      header: "Current Stage Status",
      cell: ({ row }) => {
        const instance = row.original;
        const currentStage = getAchCurrentStage(instance.StageInstances);
        if (!currentStage) {
          return <span className="text-muted-foreground">Completed</span>;
        }
        return getAchStageStatusBadge(currentStage.status);
      },
      size: 150,
    },
    {
      id: "latest_stage_user",
      header: "Current Stage Assignee",
      cell: ({ row }) => {
        const instance = row.original;
        const currentStage = getAchCurrentStage(instance.StageInstances);
        if (!currentStage) {
          return <span className="text-muted-foreground">N/A</span>;
        }
        return (
          <div className="text-sm">
            {currentStage.AssignedUser?.name || (
              <span className="text-muted-foreground">Unassigned</span>
            )}
          </div>
        );
      },
      size: 150,
    },
    {
      accessorKey: "started_at",
      header: "Started At",
      cell: ({ row }) => {
        const startedAt = row.original.started_at;
        if (!startedAt) {
          return <span className="text-muted-foreground">Not started</span>;
        }
        return (
          <div className="text-sm">
            {moment
              .tz(startedAt, CommonDestinationTimezone)
              .format("MMM DD, YYYY h:mm A")}
          </div>
        );
      },
      size: 180,
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ row }) => {
        return (
          <div className="text-sm">
            {moment
              .tz(row.original.created_at, CommonDestinationTimezone)
              .format("MMM DD, YYYY h:mm A")}
          </div>
        );
      },
      size: 180,
    },
    {
      id: "actions",
      header: "Actions",
      disableRowClick: true,
      cell: ({ row }) => {
        const instance = row.original;
        const currentStage = getAchCurrentStage(instance.StageInstances);
        const canAction =
          currentStage &&
          currentUserId &&
          currentStage.assigned_user_id === currentUserId &&
          (currentStage.status === "pending" || currentStage.status === "in_progress") &&
          (["pending", "in_progress"].includes(instance.status));

        const handleApprove = async (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!currentStage || !canAction) return;
          
          const result = await approveStage({
            stage_instance_id: currentStage.id,
            status: "approved",
          });
          
          if (result.success) {
            toast({
              title: "Stage approved",
              description: "The stage has been approved successfully.",
            });
            queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] });
          } else {
            toast({
              title: "Failed to approve stage",
              description: result.message || "An error occurred",
              variant: "destructive",
            });
          }
        };

        const handleDeny = async (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!currentStage || !canAction) return;
          
          const result = await approveStage({
            stage_instance_id: currentStage.id,
            status: "denied",
          });
          
          if (result.success) {
            toast({
              title: "Stage denied",
              description: "The stage has been denied successfully.",
            });
            queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] });
          } else {
            toast({
              title: "Failed to deny stage",
              description: result.message || "An error occurred",
              variant: "destructive",
            });
          }
        };

        return (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 px-2"
              disabled={!canAction}
              onClick={handleApprove}
              title={canAction ? "Approve stage" : "You are not assigned to this stage"}
            >
              <CheckCircle className="h-3 w-3 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              className="h-7 px-2"
              disabled={!canAction}
              onClick={handleDeny}
              title={canAction ? "Deny stage" : "You are not assigned to this stage"}
            >
              <XCircle className="h-3 w-3 mr-1" />
              Deny
            </Button>
          </div>
        );
      },
      size: 180,
    },
    {
      id: "sent_preview",
      accessorKey: "sent_preview",
      header: "Sent Document",
      accessorFn: (row) => row.AchDocumentInstance?.DocumentTemplateInstance?.SigningSession?.sent_document_id,
      cell: (info) => {
        const instance = info.row.original;
        const id = info.getValue() as string;
        return (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              navigateToPreview(instance, "sent");
            }}
            disabled={!id}
          >
            <span className="sr-only">Preview Sent Document</span>
            <FileIcon className="h-4 w-4" />
          </Button>
        );
      },
      enableFiltering: false,
      disableRowClick: true,
      size: 120,
    },
    {
      id: "final_preview",
      accessorKey: "final_preview",
      header: "Final Document",
      accessorFn: (row) => row.AchDocumentInstance?.DocumentTemplateInstance?.SigningSession?.final_document_id,
      cell: (info) => {
        const instance = info.row.original;
        const id = info.getValue() as string;
        return (
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation();
              navigateToPreview(instance, "final");
            }}
            disabled={!id}
          >
            <span className="sr-only">Preview Final Document</span>
            <FileIcon className="h-4 w-4" />
          </Button>
        );
      },
      enableFiltering: false,
      disableRowClick: true,
      size: 120,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-wide">
            ACH
          </p>
          <h2 className="text-2xl font-bold">Review Instances</h2>
        </div>
      </div>

      <div className="space-y-4">
        <DataTable
          defaultSorting={[{ id: "created_at", desc: true }]}
          data={instances}
          columns={columns}
          stickyHeaders
          enableSorting
          enableFiltering
          filterStyle="select"
          filterMode="internal"
          emptyMessage={isLoading ? "Loading..." : "No review instances found"}
          tableClassName="min-h-[600px] max-h-[70vh] overflow-y-auto"
          rowClickable
          onRowClick={(row) => {
            setSelectedInstance(row as DocumentReviewInstanceSummary);
          }}
        />

        {total > limit && (
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {Math.min((page - 1) * limit + 1, total)} to{" "}
              {Math.min(page * limit, total)} of {total} instances
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                disabled={page * limit >= total || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {selectedInstance && (
        <ReviewInstanceModal
          instance={selectedInstance}
          currentUserId={currentUserId}
          onClose={() => setSelectedInstance(null)}
          onApprove={async (payload) => {
            const result = await approveStage(payload);
            if (result.success) {
              toast({
                title: "Stage action completed",
                description: "The stage has been updated successfully.",
              });
              queryClient.invalidateQueries({ queryKey: ["ach", "review-instances"] });
              setSelectedInstance(null);
            } else {
              toast({
                title: "Failed to complete action",
                description: result.message || "An error occurred",
                variant: "destructive",
              });
            }
          }}
        />
      )}
    </div>
  );
}

interface ReviewInstanceModalProps {
  instance: DocumentReviewInstanceSummary;
  currentUserId: string | null;
  onClose: () => void;
  onApprove: (payload: ApproveAchReviewStageInstancePayload) => Promise<void>;
}

function ReviewInstanceModal({
  instance,
  currentUserId,
  onClose,
  onApprove,
}: ReviewInstanceModalProps) {
  const [actionStatus, setActionStatus] = useState<"approved" | "denied" | null>(
    null
  );
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedStages = useMemo(() => {
    return [...instance.StageInstances].sort(
      (a, b) => a.ReviewStage.stage_order - b.ReviewStage.stage_order
    );
  }, [instance.StageInstances]);

  const currentStage = getAchCurrentStage(instance.StageInstances);
  const canAction =
    currentStage &&
    currentUserId &&
    currentStage.assigned_user_id === currentUserId &&
    (currentStage.status === "pending" || currentStage.status === "in_progress");

  const handleSubmit = async () => {
    if (!actionStatus || !currentStage) return;

    setIsSubmitting(true);
    try {
      await onApprove({
        stage_instance_id: currentStage.id,
        status: actionStatus,
        reason: reason || undefined,
        notes: notes || undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Instance Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Instance Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Review Type</Label>
              <div className="font-medium">{instance.Workflow.review_type}</div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <div>{getAchInstanceStatusBadge(instance.status)}</div>
            </div>
            {instance.AchDocumentInstance && (
              <>
                <div>
                  <Label className="text-xs text-muted-foreground">Customer</Label>
                  <div className="font-medium">
                    {instance.AchDocumentInstance.customer_name}
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Customer Email</Label>
                  <div className="text-sm">{instance.AchDocumentInstance.customer_email}</div>
                </div>
              </>
            )}
          </div>

          {/* Stages List */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">Stages</Label>
            <div className="space-y-3">
              {sortedStages.map((stage) => {
                const isCurrentStage = stage.id === currentStage?.id;
                const isUserStage =
                  currentUserId && stage.assigned_user_id === currentUserId;
                const isCompleted =
                  stage.status === "approved" ||
                  stage.status === "denied" ||
                  stage.status === "skipped";

                return (
                  <div
                    key={stage.id}
                    className={`border rounded-lg p-4 ${
                      isCurrentStage ? "border-blue-500 bg-blue-50" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">
                            Stage {stage.ReviewStage.stage_order}
                            {stage.ReviewStage.name && `: ${stage.ReviewStage.name}`}
                          </span>
                          {getAchStageStatusBadge(stage.status)}
                          {isCurrentStage && (
                            <Badge variant="outline" className="bg-blue-100 text-blue-700">
                              Current
                            </Badge>
                          )}
                          {isUserStage && (
                            <Badge variant="outline" className="bg-green-100 text-green-700">
                              Your Stage
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div>
                            Assigned to:{" "}
                            {stage.AssignedUser?.name || (
                              <span className="text-red-500">Unassigned</span>
                            )}
                          </div>
                          {stage.started_at && (
                            <div>
                              Started:{" "}
                              {moment
                                .tz(stage.started_at, CommonDestinationTimezone)
                                .format("MMM DD, YYYY h:mm A")}
                            </div>
                          )}
                          {stage.completed_at && (
                            <div>
                              Completed:{" "}
                              {moment
                                .tz(stage.completed_at, CommonDestinationTimezone)
                                .format("MMM DD, YYYY h:mm A")}
                            </div>
                          )}
                          {stage.ReviewedBy && (
                            <div>Reviewed by: {stage.ReviewedBy.name}</div>
                          )}
                          {stage.reason && (
                            <div>
                              <span className="font-medium">Reason:</span> {stage.reason}
                            </div>
                          )}
                          {stage.notes && (
                            <div>
                              <span className="font-medium">Notes:</span> {stage.notes}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Form (only if user can action) */}
          {canAction && currentStage && (
            <div className="border-t pt-4">
              <Label className="text-sm font-semibold mb-3 block">Take Action</Label>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={actionStatus === "approved" ? "default" : "outline"}
                    onClick={() => setActionStatus("approved")}
                    disabled={isSubmitting}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant={actionStatus === "denied" ? "destructive" : "outline"}
                    onClick={() => setActionStatus("denied")}
                    disabled={isSubmitting}
                  >
                    <XCircle className="mr-2 h-4 w-4" />
                    Deny
                  </Button>
                </div>

                {actionStatus && (
                  <>
                    <div>
                      <Label htmlFor="reason">Reason (optional)</Label>
                      <Input
                        id="reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for this action"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Enter additional notes"
                        rows={3}
                        disabled={isSubmitting}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Close
          </Button>
          {canAction && actionStatus && (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Action"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

